/* eslint-disable no-unused-expressions */
/**
 * @file AppointmentsScheduleView.jsx
 * @author Fernando Ruano
 * @description Appointments Schedule View
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { ArrowRightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Row, Col, Modal, Button } from 'antd';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

// Helpers
import { capitalizeFirstLetter } from '@nephro/commons/helpers/string-helper';
import { getStorageItem } from '@nephro/commons/helpers/storage-helper';

// Styles
import styles from './styles.module.css';

const AppointmentsScheduleView = ({
  location,
  appointmentsZoomActions,
  actions
}) => {
  const [currentQuotes, setCurrentQuotes] = React.useState(
    location.state.selected
  );
  const [visible, setVisible] = React.useState(false);
  const [infoQuote, setInfoQuote] = React.useState(false);
  const [agendaMatePatient, setAgendaMatePatient] = React.useState(false);
  const [nephroPatient, setNephroPatient] = React.useState(false);
  const { t: translate } = useTranslation();
  const history = useHistory();

  const { setLoading } = actions;
  const { state } = location;

  const {
    scheduleAppointmentZoom,
    reScheduleAppointmentZoom,
    getZoomVideoCallById
  } = appointmentsZoomActions;

  React.useEffect(() => {
    const getPatientInfo = async () => {
      const agendaMatePatientInfo = await getStorageItem(
        'agendaMatePatientInfo'
      );
      const patient = await getStorageItem('user');
      setAgendaMatePatient(agendaMatePatientInfo);
      setNephroPatient(patient);
    };
    getPatientInfo();
    setCurrentQuotes(state.selected);
  }, []);

  const onResult = (type, _, response) => {
    if (type === 'error') {
      Modal.error({
        title: translate('calendarMg.systemMsg'),
        content: response?.title
      });
    } else {
      Modal.success({
        title: translate('calendarMg.systemMsg'),
        content: 'Se ha agendado una nueva cita',
        onOk: () => history.push(`/main/appointments`)
      });
    }
  };

  const onResultReschedule = (type, _, response) => {
    if (type === 'error') {
      Modal.error({
        title: translate('calendarMg.systemMsg'),
        content: response?.title
      });
    } else {
      Modal.success({
        title: translate('calendarMg.systemMsg'),
        content: 'Se ha reagendado su cita',
        onOk: () => history.push(`/main/appointments`)
      });
    }
  };

  const onScheduleAppointment = async () => {
    const data = {
      capId: infoQuote.id,
      idPatientAm: agendaMatePatient.id,
      idPatient: nephroPatient.id,
      tipoConsultaId: infoQuote.tipoConsultaId,
      profesional: infoQuote.profesional,
      capHorainicio: infoQuote.cap_horainicio
    };
    scheduleAppointmentZoom(data, onResult);
    setVisible(false);
  };

  const onGetVideoCallInfo = async (_, resp) => {
    const meetingId = resp?.data?.id_zoom_call || '';
    await setLoading(true);
    const data = {
      capHorainicio: infoQuote.cap_horainicio,
      capId: infoQuote.id,
      profesional: infoQuote.profesional,
      tipoConsultaId: infoQuote.tipoConsultaId,
      idPatientAm: agendaMatePatient.id,
      quoteId: state.toSchedule.id,
      meetingId
    };
    reScheduleAppointmentZoom(data, onResultReschedule);
    setVisible(false);
  };

  const onReScheduleAppointment = () => {
    const data = { quoteId: state.toSchedule.id };
    getZoomVideoCallById(data, onGetVideoCallInfo);
  };

  const onSelectSlot = (itm) => {
    setInfoQuote(itm);
    setVisible(true);
  };

  const dateCard = (itm) => (
    <div onClick={() => onSelectSlot(itm)} className={styles.appointmentCard}>
      <div>
        <h2 className="mb-0 text-base font-bold text-baxterGray">
          {moment(itm.cap_hora, 'HH:mm').format('h:mm a')}
        </h2>
        <h2 className="mb-0 text-base text-baxterGray">{itm.profesional}</h2>
        <h2 className="mb-0 text-baxterGray text-base">{itm.cr_direccion}</h2>
      </div>
      <div>
        <ArrowRightOutlined
          style={{ color: 'var(--color-grayFontInput)', fontSize: 20 }}
        />
      </div>
    </div>
  );

  return (
    <div className="h-full p-5">
      <h2 className="mb-0 text-base text-baxterGray">
        {translate('calendarMg.availableText')}
      </h2>
      <h2 className="mb-0 text-lg text-baxterGray mt-2 font-bold">
        {currentQuotes[0]?.tc_tipoconsulta} - {currentQuotes[0]?.modalidad}
      </h2>
      <h2 className="mb-0 text-lg text-baxterGray font-bold">
        {capitalizeFirstLetter(
          moment(currentQuotes[0]?.cap_fecha).format('MMMM D, YYYY')
        )}
      </h2>
      {currentQuotes.length > 0 && (
        <>
          <h2 className="mb-3 mt-2 text-base text-baxterGray">
            {translate('calendarMg.select')}
          </h2>
          <Row gutter={[7, 7]}>
            {currentQuotes.map((itm) => {
              return (
                <Col key={itm.id} xs={24} sm={12} md={8} lg={8} xl={8}>
                  {dateCard(itm)}
                </Col>
              );
            })}
          </Row>
          <div className="h-5" />
        </>
      )}
      <Modal visible={visible} title="" closable={false} footer={null}>
        <div className="text-center">
          <InfoCircleOutlined
            style={{ color: 'var(--color-baxter-teal-medium)', fontSize: 88 }}
          />
          <h2 className="mb-0 mt-4 text-2xl font-bold text-baxterGray">
            Confirma la información
          </h2>
          <h2 className="mb-0 text-lg text-baxterGray">
            {infoQuote.tc_tipoconsulta}
          </h2>
          <h2 className="mb-0 text-baxterGray text-base">
            {infoQuote.profesional}
          </h2>
          <h2 className="mb-0 text-baxterGray text-base">
            {capitalizeFirstLetter(
              moment(currentQuotes[0]?.cap_fecha).format('MMMM D, YYYY')
            )}{' '}
            - {moment(infoQuote.cap_hora, 'HH:mm').format('h:mm a')}
          </h2>
          <h2 className="mb-0 text-baxterGray text-base">
            {infoQuote.cr_direccion}
          </h2>
          <div className="w-full flex flex-row justify-center mt-6">
            <Button
              className={styles.cancelButton}
              onClick={() => setVisible(false)}>
              {translate('commons.cancel')}
            </Button>
            <Button
              className={styles.okButton}
              onClick={() => {
                state.toSchedule
                  ? onReScheduleAppointment()
                  : onScheduleAppointment();
              }}>
              {translate(
                `${
                  state.toSchedule
                    ? 'dashboard.reschedule'
                    : 'dashboard.confirmDate'
                }`
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

/**
 * Component definition of proptypes.
 */
AppointmentsScheduleView.propTypes = {
  actions: PropTypes.shape({
    setLoading: PropTypes.func.isRequired
  }).isRequired,
  appointmentsZoomActions: PropTypes.shape({
    scheduleAppointmentZoom: PropTypes.func.isRequired,
    reScheduleAppointmentZoom: PropTypes.func.isRequired,
    getZoomVideoCallById: PropTypes.func.isRequired
  }).isRequired,
  location: PropTypes.shape({
    state: PropTypes.array.isRequired
  }).isRequired,
  loading: PropTypes.bool
};

/**
 * Initialize default props
 */
AppointmentsScheduleView.defaultProps = {};

export default AppointmentsScheduleView;
